import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useState, useEffect } from 'react';
import { t } from '@/utils/i18n';
import { userService } from '@/services/userService';
import { useCustomAuth } from '@/contexts/CustomAuthContext';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { toast } from '@/hooks/use-toast';
import { Loader2, User, Eye, EyeOff, Camera } from 'lucide-react';
import { z } from 'zod';
import { toast as sonnerToast } from 'sonner';

const createPersonalInfoSchema = () => z.object({
  nome: z.string().trim().min(1, { message: translateInterfaceText("Nome não pode estar vazio") }).max(100, { message: translateInterfaceText("Nome muito longo") }),
  email: z.string().optional(),
  telefone: z.string().optional(),
  cpf: z.string().optional(),
});

const createPasswordSchema = () => z.object({
  currentPassword: z.string().min(1, { message: translateInterfaceText("Senha atual é obrigatória") }),
  newPassword: z.string()
    .min(8, { message: translateInterfaceText("Senha deve ter pelo menos 8 caracteres") })
    .regex(/[A-Z]/, { message: translateInterfaceText("Deve conter letra maiúscula") })
    .regex(/[a-z]/, { message: translateInterfaceText("Deve conter letra minúscula") })
    .regex(/[0-9]/, { message: translateInterfaceText("Deve conter número") }),
  confirmPassword: z.string()
}).refine(data => data.newPassword === data.confirmPassword, {
  message: translateInterfaceText("As senhas não conferem"),
  path: ['confirmPassword']
}).refine(data => data.currentPassword !== data.newPassword, {
  message: translateInterfaceText("Nova senha deve ser diferente da atual"),
  path: ['newPassword']
});

export function ProfileUnifiedCard() {
  useLanguage();
  const { user, refreshUser } = useCustomAuth();

  // Personal Info state
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [nome, setNome] = useState('');
  const [telefone, setTelefone] = useState('');
  const [cpf, setCpf] = useState('');

  const [disabeleCpf, setDisabeleCpf] = useState(true);

  const [personalLoading, setPersonalLoading] = useState(false);
  const [personalErrors, setPersonalErrors] = useState<Record<string, string>>({});

  // Security state
  const [securityLoading, setSecurityLoading] = useState(false);
  const [securityErrors, setSecurityErrors] = useState<Record<string, string>>({});
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Preencher dados do usuário ao carregar
  useEffect(() => {
    if (user) {
      // Atualizar avatar (incluindo quando for null/removido)
      setAvatarUrl(user.avatar || null);

      // Atualizar nome
      setNome(user.nome || '');

      // Atualizar telefone
      setTelefone(user.telefone || '');

      // Atualizar telefone
      setCpf(user.cpf || '');
      setDisabeleCpf(!!user.cpf)
    }
  }, [user]);

  const handleAvatarChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && user?.id_user) {
      setPersonalLoading(true);
      try {
        // Upload para o servidor
        const response = await userService.updateAvatar(user.id_user, file);

        // Backend retorna { success: true, mensagem: '...', avatarUrl: '...' }
        if (response?.avatarUrl) {
          setAvatarUrl(response.avatarUrl);
        }

        sonnerToast.success(response?.mensagem || translateInterfaceText("Avatar atualizado com sucesso!"));

        // Atualizar dados do usuário no contexto
        await refreshUser();

        // Resetar input para permitir selecionar o mesmo arquivo novamente
        event.target.value = '';
      } catch (error: any) {
        sonnerToast.error(error?.response?.data?.error || error?.response?.data?.message || translateInterfaceText("Erro ao atualizar avatar"));
        event.target.value = '';
      } finally {
        setPersonalLoading(false);
      }
    }
  };

  const handlePersonalSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!user?.id_user) {
      sonnerToast.error('Usuário não autenticado');
      return;
    }

    setPersonalLoading(true);
    setPersonalErrors({});

    const data = {
      nome: nome,
      email: user.email,
      telefone: telefone || '',
      cpf: cpf || '',
    };

    try {
      // Validar dados
      createPersonalInfoSchema().parse(data);

      // Enviar para o backend
      const response = await userService.updateUser(user.id_user, {
        nome: data.nome,
        email: data.email,
        telefone: data.telefone || undefined,
        cpf: data.cpf || undefined,
      });

      sonnerToast.success(response?.mensagem || translateInterfaceText("Informações atualizadas com sucesso!"));
      await refreshUser();
    } catch (error: any) {
      if (error instanceof z.ZodError) {
        const fieldErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) fieldErrors[err.path[0] as string] = err.message;
        });
        setPersonalErrors(fieldErrors);
      } else {
        console.error('Erro ao atualizar:', error);
        sonnerToast.error(error?.response?.data?.message || translateInterfaceText("Erro ao atualizar informações"));
      }
    } finally {
      setPersonalLoading(false);
    }
  };

  const handleSecuritySubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!user?.id_user) {
      sonnerToast.error('Usuário não autenticado');
      return;
    }

    setSecurityLoading(true);
    setSecurityErrors({});

    const formData = new FormData(e.currentTarget);
    const data = {
      currentPassword: formData.get('current-password') as string,
      newPassword: formData.get('new-password') as string,
      confirmPassword: formData.get('confirm-password') as string,
    };

    try {
      // Validar dados
      createPasswordSchema().parse(data);

      // Enviar para o backend
      const response = await userService.changePassword({
        id_user: user.id_user,
        senhaAntiga: data.currentPassword,
        senha: data.newPassword,
        confirmSenha: data.confirmPassword,
      });

      if (response.success) {
        sonnerToast.success(translateInterfaceText("Senha atualizada com sucesso!"));
        (e.target as HTMLFormElement).reset();
      } else {
        sonnerToast.error(response.message || translateInterfaceText("Erro ao atualizar senha"));
      }
    } catch (error: any) {
      if (error instanceof z.ZodError) {
        const fieldErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) fieldErrors[err.path[0] as string] = err.message;
        });
        setSecurityErrors(fieldErrors);
      } else {
        console.error('Erro ao atualizar senha:', error);
        sonnerToast.error(error?.response?.data?.message || translateInterfaceText("Erro ao atualizar senha"));
      }
    } finally {
      setSecurityLoading(false);
    }
  };

  return (
    <>
      {/* Grid 2 colunas lado a lado */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Card: Informações Pessoais */}
        <div className="border border-[#E5E7EB] rounded-[14px] bg-card shadow-[0_4px_12px_rgba(0,0,0,0.04)] h-full">
          <form onSubmit={handlePersonalSubmit}>
            <div className="p-5">
              {/* Header com título à esquerda e avatar à direita */}
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-[18px] font-semibold text-foreground">{t('personalInfoTitle')}</h2>
                  <p className="text-[13px] text-[#6B7280] mt-1">{t('personalInfoSubtitle')}</p>
                </div>

                {/* Avatar à direita */}
                <div className="flex flex-col items-center gap-1.5">
                  {avatarUrl ? (
                    // Quando tem foto: hover com ícone de câmera e texto
                    <Label htmlFor="avatar-with-photo" className="relative cursor-pointer group" title={translateInterfaceText("Alterar foto")}>
                      <Avatar className="w-20 h-20 transition-all">
                        <AvatarImage src={avatarUrl} alt={translateInterfaceText("Avatar")} className="group-hover:opacity-70 transition-opacity" />
                      </Avatar>
                      {/* Overlay com ícone de câmera e texto ao passar o mouse */}
                      <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                        <Camera className="w-6 h-6 text-white mb-1" />
                        <span className="text-white text-[10px] font-medium">{translateInterfaceText("Alterar foto")}</span>
                      </div>
                      <Input
                        id="avatar-with-photo"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleAvatarChange}
                        disabled={personalLoading}
                      />
                    </Label>
                  ) : (
                    // Quando não tem foto: manter comportamento atual
                    <>
                      <Avatar className="w-20 h-20">
                        <AvatarFallback className="bg-muted">
                          <User className="w-5 h-5 text-muted-foreground" />
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex gap-2 items-center text-[11px]">
                        <Label htmlFor="avatar-no-photo" className="cursor-pointer text-primary hover:underline">
                          {t('changePhoto')}
                          <Input
                            id="avatar-no-photo"
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={handleAvatarChange}
                            disabled={personalLoading}
                          />
                        </Label>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Campos */}
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label htmlFor="nome" className="text-[13px]">{translateInterfaceText("Nome")}</Label>
                  <Input
                    id="nome"
                    name="nome"
                    value={nome}
                    onChange={(e) => setNome(e.target.value)}
                    disabled={personalLoading}
                    className="h-10"
                    aria-invalid={!!personalErrors.nome}
                  />
                  {personalErrors.nome && <p className="text-xs text-destructive">{personalErrors.nome}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-[13px]">{t('email')}</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={user?.email || ''}
                    disabled={true}
                    className="h-10 bg-muted cursor-not-allowed"
                    aria-invalid={!!personalErrors.email}
                  />
                  {personalErrors.email && <p className="text-xs text-destructive">{personalErrors.email}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="telefone" className="text-[13px]">{t('phone')}</Label>
                  <Input
                    id="telefone"
                    name="telefone"
                    type="tel"
                    value={telefone}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, '').slice(0, 11);
                      let formatted = digits;
                      if (digits.length > 2) formatted = `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
                      if (digits.length > 7) formatted = `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
                      setTelefone(formatted);
                    }}
                    placeholder="(11) 99999-9999"
                    disabled={personalLoading}
                    className="h-10"
                    aria-invalid={!!personalErrors.telefone}
                  />
                  {personalErrors.telefone && <p className="text-xs text-destructive">{personalErrors.telefone}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="telefone" className="text-[13px]">{t('Cpf')}</Label>
                  <Input
                    id="cpf"
                    name="cpf"
                    type="tel"
                    value={cpf}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, '').slice(0, 11);
                      setCpf(digits);
                    }}
                    placeholder="000.000.000-00"
                    disabled={disabeleCpf}
                    className="h-10"
                    aria-invalid={!!personalErrors.cpf}
                  />
                  {personalErrors.telefone && <p className="text-xs text-destructive">{personalErrors.telefone}</p>}
                </div>
              </div>

              <div className="flex justify-end mt-5">
                <Button type="submit" disabled={personalLoading} className="min-w-[140px] h-10">
                  {personalLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {t('savePersonalInfo')}
                </Button>
              </div>
            </div>
          </form>
        </div>

        {/* Card: Senha de Acesso */}
        <div className="border border-[#E5E7EB] rounded-[14px] bg-card shadow-[0_4px_12px_rgba(0,0,0,0.04)] h-full">
          <form onSubmit={handleSecuritySubmit}>
            <div className="p-5">
              <div className="pb-4">
                <h2 className="text-[18px] font-semibold text-foreground">{t('securityTitle')}</h2>
                <p className="text-[13px] text-[#6B7280] mt-1">{t('securitySubtitle')}</p>
              </div>

              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label htmlFor="current-password" className="text-[13px]">{t('currentPassword')}</Label>
                  <div className="relative">
                    <Input id="current-password" name="current-password" type={showCurrentPassword ? "text" : "password"} required disabled={securityLoading} className="h-10 pr-10" aria-invalid={!!securityErrors.currentPassword} />
                    <button type="button" onClick={() => setShowCurrentPassword(!showCurrentPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" disabled={securityLoading}>
                      {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {securityErrors.currentPassword && <p className="text-xs text-destructive">{securityErrors.currentPassword}</p>}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="new-password" className="text-[13px]">{t('newPassword')}</Label>
                  <div className="relative">
                    <Input id="new-password" name="new-password" type={showNewPassword ? "text" : "password"} required disabled={securityLoading} className="h-10 pr-10" aria-invalid={!!securityErrors.newPassword} />
                    <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" disabled={securityLoading}>
                      {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {securityErrors.newPassword && <p className="text-xs text-destructive">{securityErrors.newPassword}</p>}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="confirm-password" className="text-[13px]">{t('confirmNewPassword')}</Label>
                  <div className="relative">
                    <Input id="confirm-password" name="confirm-password" type={showConfirmPassword ? "text" : "password"} required disabled={securityLoading} className="h-10 pr-10" aria-invalid={!!securityErrors.confirmPassword} />
                    <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" disabled={securityLoading}>
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {securityErrors.confirmPassword && <p className="text-xs text-destructive">{securityErrors.confirmPassword}</p>}
                </div>

                <p className="text-[12px] text-[#6B7280] pt-1">{t('passwordRequirements')}</p>
              </div>

              <div className="flex justify-end mt-5">
                <Button type="submit" disabled={securityLoading} className="min-w-[140px] h-10">
                  {securityLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {t('changePassword')}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}